import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import Avatar from '../../../base/avatar/components/Avatar';
import {
    getButtonNotifyMode,
    getParticipantMenuButtonsWithNotifyClick
} from '../../../base/config/functions.web';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import ContextMenuItemGroup from '../../../base/ui/components/web/ContextMenuItemGroup';
import { AVATAR_SIZE, PARTICIPANT_MENU_BUTTONS as BUTTONS, NOTIFY_CLICK_MODE } from '../../../breakout-rooms/constants';
import { getBreakoutRooms } from '../../../breakout-rooms/functions';

import SendToRoomButton from './SendToRoomButton';

const useStyles = makeStyles()(() => {
    return {
        text: {
            color: '#C2C2C2',
            padding: '10px 16px',
            height: '40px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box'
        }
    };
});

export const RoomParticipantContextMenu = ({
    participant,
    offsetTarget,
    onEnter,
    onLeave,
    onSelect
}) => {
    const { classes: styles } = useStyles();
    const { t } = useTranslation();
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const lowerMenu = useCallback(() => onSelect(true), [ onSelect ]);
    const rooms = useSelector(getBreakoutRooms);
    const buttonsWithNotifyClick = useSelector(getParticipantMenuButtonsWithNotifyClick);

    const notifyClick = useCallback(
        (buttonKey, participantId) => {
            const notifyMode = getButtonNotifyMode(buttonKey, buttonsWithNotifyClick);

            if (!notifyMode) {
                return;
            }

            APP.API.notifyParticipantMenuButtonClicked(
                buttonKey,
                participantId,
                notifyMode === NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY
            );
        }, [ buttonsWithNotifyClick, getButtonNotifyMode ]);

    const breakoutRoomsButtons = useMemo(() => Object.values(rooms || {}).map(room => {
        if (room.id !== participant?.room?.id) {
            return (<SendToRoomButton
                key = { room.id }

                notifyClick = { () => notifyClick(BUTTONS.SEND_PARTICIPANT_TO_ROOM, participant?.jid) }
                notifyMode = { getButtonNotifyMode(BUTTONS.SEND_PARTICIPANT_TO_ROOM, buttonsWithNotifyClick) }
                onClick = { lowerMenu }
                participantID = { participant?.jid ?? '' }
                room = { room } />);
        }

        return null;
    })
    .filter(Boolean), [ participant, rooms ]);

    return isLocalModerator ? (
        <>
            <ContextMenu
                activateFocusTrap = { true }
                entity = { participant }
                isDrawerOpen = { Boolean(participant) }
                offsetTarget = { offsetTarget }
                onClick = { lowerMenu }
                onDrawerClose = { onSelect }
                onMouseEnter = { onEnter }
                onMouseLeave = { onLeave }>
                { participant?.jid && <ContextMenuItemGroup
                    actions = { [ {
                        accessibilityLabel: participant?.participantName,
                        customIcon: <Avatar
                            displayName = { participant?.participantName }
                            size = { AVATAR_SIZE } />,
                        text: participant?.participantName
                    } ] } />}
                <ContextMenuItemGroup>
                    <div className = { styles.text }>
                        {t('breakoutRooms.actions.sendToBreakoutRoom')}
                    </div>
                    {breakoutRoomsButtons}
                </ContextMenuItemGroup>
            </ContextMenu>
        </>
    ) : null;
};
